'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { Button, IconButton } from '../../actions/button/index.js';
import { SegmentedControl } from '../../forms/segmented-control/index.js';
import { cx } from '../../../internal/cx.js';
import { RichTextEditor, } from '../rich-text-editor/index.js';
import { CloseGlyph, PaperclipGlyph, PencilGlyph, ReplyGlyph, SendGlyph, } from '../../../internal/editor-glyphs.js';
const MOD = typeof navigator !== 'undefined' && /Mac|iP(hone|ad)/.test(navigator.platform) ? '⌘' : 'Ctrl';
const DEFAULT_MESSAGES = {
    send: 'Send',
    save: 'Save',
    sending: 'Sending…',
    cancel: 'Cancel',
    attach: 'Attach files',
    modes: 'Message type',
    replyingTo: (author) => `Replying to ${author}`,
    dismissReply: 'Stop replying',
    editing: 'Editing message',
    hintEnter: 'Enter to send, Shift+Enter for a new line',
    hintModEnter: `${MOD}+Enter to send`,
    hintEditEnter: 'Enter to save, Escape to cancel',
    hintEditModEnter: `${MOD}+Enter to save, Escape to cancel`,
    draftSaving: 'Saving draft…',
    draftSaved: 'Draft saved',
    draftError: 'Draft not saved',
};
/** The composer's default toolbar: inline formatting, lists, links, quote, code. */
export const MESSAGE_COMPOSER_TOOLBAR = [
    'bold',
    'italic',
    'strike',
    'code',
    '|',
    'bulletList',
    'orderedList',
    '|',
    'link',
    'blockquote',
    'codeBlock',
];
const isPromise = (v) => typeof v === 'object' && v !== null && typeof v.then === 'function';
/**
 * The chat and ticket reply box: a compact RichTextEditor with Enter to send,
 * an attachment row, emoji and action slots, a send button with a pending
 * state, draft persistence, edit and reply-to modes, typing callbacks and a
 * product-supplied mode switch (public reply / internal note).
 */
export const MessageComposer = forwardRef(function MessageComposer({ label, placeholder, value, defaultValue, onValueChange, onSend, sending, sendOn = 'enter', sendLabel, hasAttachments = false, attachments, onFiles, fileAccept, emoji, actions, onDraftChange, draftDebounceMs = 600, draftStatus = 'idle', editing = false, onCancelEdit, replyTo, onDismissReply, onTyping, onTypingStop, typingIntervalMs = 3000, onEditLast, disabled = false, disabledReason, modes, mode: modeProp, defaultMode, onModeChange, modeAside, maxLength, showCharacterCount, mentions, toolbar = MESSAGE_COMPOSER_TOOLBAR, showHint = true, autoFocus, editorRef, messages: messagesProp, className, ...rest }, ref) {
    const m = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const id = `mn-composer-${useId().replace(/:/g, '')}`;
    const handle = useRef(null);
    useImperativeHandle(editorRef, () => handle.current, []);
    const fileInput = useRef(null);
    const [pendingState, setPending] = useState(false);
    const pending = sending ?? pendingState;
    const [empty, setEmpty] = useState(() => !(value ?? defaultValue));
    const [modeState, setModeState] = useState(defaultMode ?? modes?.[0]?.value);
    const mode = modeProp ?? modeState;
    const current = modes?.find((x) => x.value === mode);
    const draftTimer = useRef(undefined);
    const draftPending = useRef(null);
    const lastTyping = useRef(0);
    const typingActive = useRef(false);
    const live = useRef({ onDraftChange, onTyping, onTypingStop });
    live.current = { onDraftChange, onTyping, onTypingStop };
    const flushDraft = () => {
        clearTimeout(draftTimer.current);
        if (draftPending.current !== null) {
            const html = draftPending.current;
            draftPending.current = null;
            live.current.onDraftChange?.(html);
        }
    };
    // Never lose a draft that was still waiting for its debounce.
    useEffect(() => () => flushDraft(), []);
    const stopTyping = () => {
        if (!typingActive.current)
            return;
        typingActive.current = false;
        lastTyping.current = 0;
        live.current.onTypingStop?.();
    };
    const isEmpty = value !== undefined ? value === '' || value === '<p></p>' : empty;
    const canSend = !disabled && !pending && (!isEmpty || hasAttachments);
    const send = () => {
        const h = handle.current;
        if (!h || !canSend)
            return;
        const message = {
            html: h.getHTML(),
            text: h.getText(),
            json: h.isEmpty() ? null : h.getJSON(),
            ...(mode !== undefined ? { mode } : {}),
        };
        stopTyping();
        const done = () => {
            h.clear();
            clearTimeout(draftTimer.current);
            draftPending.current = null;
            live.current.onDraftChange?.('');
        };
        let result;
        try {
            result = onSend(message);
        }
        catch {
            return;
        }
        if (isPromise(result)) {
            setPending(true);
            result.then(() => {
                setPending(false);
                done();
            }, () => setPending(false));
        }
        else
            done();
    };
    const sendRef = useRef(send);
    sendRef.current = send;
    const onEditorKeyDown = (event, editor) => {
        if (event.isComposing)
            return false;
        const mod = event.metaKey || event.ctrlKey;
        if (event.key === 'Enter' && !event.altKey) {
            const plain = !event.shiftKey && !mod;
            if (mod || (sendOn === 'enter' && plain && !editor.isActive('codeBlock'))) {
                sendRef.current();
                return true;
            }
            return false;
        }
        if (event.key === 'Escape' && editing && onCancelEdit) {
            onCancelEdit();
            return true;
        }
        if (event.key === 'ArrowUp' && onEditLast && editor.isEmpty) {
            onEditLast();
            return true;
        }
        return false;
    };
    const onChange = (v, details) => {
        const html = typeof v === 'string' ? v : '';
        setEmpty(details.isEmpty);
        onValueChange?.(html);
        if (live.current.onDraftChange) {
            draftPending.current = html;
            clearTimeout(draftTimer.current);
            draftTimer.current = setTimeout(flushDraft, draftDebounceMs);
        }
        if (details.isEmpty)
            stopTyping();
        else {
            const now = Date.now();
            if (now - lastTyping.current >= typingIntervalMs) {
                lastTyping.current = now;
                typingActive.current = true;
                live.current.onTyping?.();
            }
        }
    };
    const reasonId = `${id}-reason`;
    const hintId = `${id}-hint`;
    const describedBy = [
        disabled && disabledReason ? reasonId : undefined,
        showHint && !disabled ? hintId : undefined,
    ]
        .filter(Boolean)
        .join(' ') || undefined;
    const label_ = sendLabel ?? current?.sendLabel ?? (editing ? m.save : m.send);
    const status = draftStatus === 'saving'
        ? m.draftSaving
        : draftStatus === 'saved'
            ? m.draftSaved
            : draftStatus === 'error'
                ? m.draftError
                : '';
    const actionRow = (_jsxs(_Fragment, { children: [emoji && !disabled ? emoji((text) => handle.current?.insertText(text)) : null, onFiles ? (_jsx(IconButton, { size: "sm", label: m.attach, icon: _jsx(PaperclipGlyph, {}), disabled: disabled, onClick: () => fileInput.current?.click() })) : null, actions, editing && onCancelEdit ? (_jsx(Button, { size: "sm", variant: "ghost", onClick: onCancelEdit, children: m.cancel })) : null, _jsx(Button, { size: "sm", variant: "primary", className: "mn-message-composer__send", leadingIcon: editing ? undefined : _jsx(SendGlyph, {}), loading: pending, disabled: !canSend && !pending, onClick: send, children: pending ? m.sending : label_ })] }));
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-message-composer', className), "data-tone": current?.tone === 'note' ? 'note' : undefined, "data-editing": editing || undefined, "data-disabled": disabled || undefined, children: [modes && modes.length > 0 ? (_jsxs("div", { className: "mn-message-composer__modes", children: [_jsx(SegmentedControl, { size: "sm", "aria-label": m.modes, options: modes.map((x) => ({ value: x.value, label: x.label })), value: mode ?? modes[0]?.value ?? '', disabled: disabled, onValueChange: (v) => {
                            setModeState(v);
                            onModeChange?.(v);
                        } }), modeAside ? _jsx("div", { className: "mn-message-composer__mode-aside", children: modeAside }) : null] })) : null, editing || replyTo ? (_jsxs("div", { className: "mn-message-composer__context", children: [_jsx("span", { className: "mn-message-composer__context-icon", "aria-hidden": true, children: editing ? _jsx(PencilGlyph, {}) : _jsx(ReplyGlyph, {}) }), _jsxs("div", { className: "mn-message-composer__context-text", children: [_jsx("span", { className: "mn-message-composer__context-title", children: editing ? m.editing : replyTo ? m.replyingTo(replyTo.author) : null }), !editing && replyTo?.excerpt ? (_jsx("span", { className: "mn-message-composer__context-excerpt", children: replyTo.excerpt })) : null] }), !editing && replyTo && onDismissReply ? (_jsx(IconButton, { size: "sm", label: m.dismissReply, icon: _jsx(CloseGlyph, {}), onClick: () => {
                            onDismissReply();
                            handle.current?.focus();
                        } })) : null] })) : null, attachments ? _jsx("div", { className: "mn-message-composer__attachments", children: attachments }) : null, _jsx(RichTextEditor, { className: "mn-message-composer__editor", "aria-label": label, "aria-describedby": describedBy, editorRef: handle, ...(value !== undefined ? { value } : {}), ...(defaultValue !== undefined ? { defaultValue } : {}), onValueChange: onChange, placeholder: current?.placeholder ?? placeholder ?? '', disabled: disabled, 
                // Editing moves focus to the message being edited, as the person asked to edit it.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus: autoFocus ?? editing, toolbar: toolbar, toolbarPosition: "bottom", toolbarEnd: actionRow, headingLevels: [], taskList: false, minHeight: "2.75rem", maxHeight: "15rem", ...(maxLength !== undefined ? { maxLength } : {}), ...(showCharacterCount !== undefined ? { showCharacterCount } : {}), ...(mentions ? { mentions } : {}), ...(onFiles ? { onFiles, images: false } : {}), onEditorKeyDown: onEditorKeyDown, onBlur: (e) => {
                    if (!e.currentTarget.contains(e.relatedTarget))
                        stopTyping();
                } }), onFiles ? (_jsx("input", { ref: fileInput, type: "file", multiple: true, hidden: true, tabIndex: -1, ...(fileAccept ? { accept: fileAccept } : {}), onChange: (e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    if (files.length)
                        void onFiles(files, 'picker');
                } })) : null, _jsxs("div", { className: "mn-message-composer__footer", children: [disabled && disabledReason ? (_jsx("p", { id: reasonId, className: "mn-message-composer__reason", children: disabledReason })) : showHint ? (_jsx("p", { id: hintId, className: "mn-message-composer__hint", children: editing
                            ? sendOn === 'enter'
                                ? m.hintEditEnter
                                : m.hintEditModEnter
                            : sendOn === 'enter'
                                ? m.hintEnter
                                : m.hintModEnter })) : null, _jsx("p", { className: "mn-message-composer__status", role: "status", "data-status": draftStatus, children: status })] })] }));
});
//# sourceMappingURL=message-composer.js.map